import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  AlertCircle,
  ExternalLink,
  FileDown,
  Eye,
  Inbox,
  Loader2,
  RefreshCw,
  Search,
} from 'lucide-react';

// Public web base of Projekt-Cockpit for "Im Projekt-Cockpit öffnen" deep links (no token needed).
const COCKPIT_WEB_BASE = 'https://projekte.en-power.eu';

const VARIANT_LABELS = {
  'expose-projektverkauf': 'Exposé PV',
  'expose-turnkey': 'Exposé Turnkey',
  'expose-wirtschaftlichkeit': 'Wirtschaftlichkeit',
};

const STATUS_STYLES = {
  draft: { label: 'Entwurf', bg: '#F1F5F9', color: '#475569', border: '#D7DEE9' },
  approved: { label: 'Freigegeben', bg: '#ECFDF5', color: '#047857', border: '#A7F3D0' },
  revoked: { label: 'Widerrufen', bg: '#FEF2F2', color: '#B91C1C', border: '#FECACA' },
};

const FONT_HEAD = 'Outfit, sans-serif';
const FONT_BODY = 'Manrope, sans-serif';

function variantLabel(variantKey) {
  if (!variantKey) return 'Exposé';
  return VARIANT_LABELS[variantKey] || String(variantKey);
}

function formatPrice(value, currency) {
  const num = Number(value);
  if (!Number.isFinite(num) || value === null || value === undefined || value === '') return null;
  try {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currency || 'EUR',
      maximumFractionDigits: 0,
    }).format(num);
  } catch (_error) {
    return `${num} ${currency || 'EUR'}`;
  }
}

function formatDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  try {
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(d);
  } catch (_error) {
    return null;
  }
}

// Tolerant extraction of the project list from a variety of possible response shapes.
function extractProjects(payload) {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return [];
  const candidates = [payload.data, payload.projects, payload.exposes, payload.items, payload.results];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
  }
  // { data: { projects: [...] } } style
  if (payload.data && typeof payload.data === 'object') {
    return extractProjects(payload.data);
  }
  return [];
}

function getProjectId(project) {
  return String(project?.projectId ?? project?.project_id ?? project?.id ?? '').trim();
}

function getProjectName(project) {
  return (
    project?.projectName
    || project?.project_name
    || project?.name
    || project?.title
    || (getProjectId(project) ? `Projekt ${getProjectId(project)}` : 'Unbenanntes Projekt')
  );
}

function getBaseExpose(project) {
  const base = project?.baseExpose || project?.base || project?.expose || null;
  if (base && typeof base === 'object') {
    // Cockpit-API liefert publicTitle/publicStatus/standardPrice — auf Anzeige-Felder normalisieren.
    return {
      ...base,
      title: base.title || base.publicTitle || '',
      status: base.status || base.publicStatus || '',
      salePrice: base.salePrice ?? base.standardPrice ?? null,
    };
  }
  // Fall back to project-level meta fields as the base exposé.
  const { title, variantKey, status, salePrice, currency, approvedAt, updatedAt } = project || {};
  if (title || variantKey || status || salePrice || approvedAt || updatedAt) {
    return { title, variantKey, status, salePrice, currency, approvedAt, updatedAt };
  }
  return null;
}

function getVersions(project) {
  const versions = project?.versions || project?.exposeVersions || project?.expose_versions || [];
  return Array.isArray(versions) ? versions : [];
}

function StatusBadge({ status }) {
  const key = String(status || '').toLowerCase();
  const style = STATUS_STYLES[key] || {
    label: status ? String(status) : 'Unbekannt',
    bg: '#F1F5F9',
    color: '#475569',
    border: '#D7DEE9',
  };
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '3px 10px',
        borderRadius: 999,
        background: style.bg,
        color: style.color,
        border: `1px solid ${style.border}`,
        fontFamily: FONT_BODY,
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: 0.2,
        whiteSpace: 'nowrap',
      }}
    >
      {style.label}
    </span>
  );
}

// Sanitizes a value for use inside a client-side filename (no path separators,
// quotes or other characters filesystems / browsers dislike).
function sanitizeFilenamePart(value) {
  return String(value || '')
    .replace(/[^a-zA-Z0-9-_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// Fetches the exposé document with the caller's auth header (the plain
// `window.open` used before this component required auth cannot send an
// Authorization header, so the doc is streamed via axios as a blob and
// handed to the browser through an object URL instead).
async function downloadCockpitDocument({ apiBase, authHeaders, params, format, baseName }) {
  const search = new URLSearchParams({ ...params, format }).toString();
  const response = await axios.get(`${apiBase}/cockpit/expose-document?${search}`, {
    headers: authHeaders,
    responseType: 'blob',
  });

  const ext = format === 'html' ? 'html' : 'pdf';
  const filename = `${baseName || 'expose'}.${ext}`;
  const objectUrl = URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = objectUrl;
  if (format === 'html') {
    // "Ansehen" — open inline in a new tab instead of forcing a download.
    link.target = '_blank';
    link.rel = 'noopener';
  } else {
    link.download = filename;
  }
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 2000);
}

function DocButtons({ apiBase, authHeaders, params, baseName }) {
  const [loadingFormat, setLoadingFormat] = useState(null);

  const openDoc = async (format) => {
    if (loadingFormat) return;
    setLoadingFormat(format);
    try {
      await downloadCockpitDocument({ apiBase, authHeaders, params, format, baseName });
    } catch (_error) {
      toast.error('Dokument konnte nicht geladen werden.');
    } finally {
      setLoadingFormat(null);
    }
  };
  const btnBase = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    borderRadius: 999,
    fontFamily: FONT_BODY,
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
  };
  return (
    <div style={{ display: 'inline-flex', gap: 8 }}>
      <button
        type="button"
        onClick={() => openDoc('pdf')}
        disabled={loadingFormat !== null}
        style={{
          ...btnBase,
          border: '1px solid #122A5C',
          background: '#122A5C',
          color: '#FFFFFF',
          opacity: loadingFormat && loadingFormat !== 'pdf' ? 0.6 : 1,
          cursor: loadingFormat ? 'not-allowed' : 'pointer',
        }}
      >
        {loadingFormat === 'pdf' ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />} PDF
      </button>
      <button
        type="button"
        onClick={() => openDoc('html')}
        disabled={loadingFormat !== null}
        style={{
          ...btnBase,
          border: '1px solid #D7DEE9',
          background: '#FFFFFF',
          color: '#0E214A',
          opacity: loadingFormat && loadingFormat !== 'html' ? 0.6 : 1,
          cursor: loadingFormat ? 'not-allowed' : 'pointer',
        }}
      >
        {loadingFormat === 'html' ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />} Ansehen
      </button>
    </div>
  );
}

function MetaLine({ price, date }) {
  const parts = [];
  if (price) parts.push(price);
  if (date) parts.push(`Aktualisiert ${date}`);
  if (parts.length === 0) return null;
  return (
    <span style={{ fontFamily: FONT_BODY, fontSize: 12, color: '#64748B' }}>{parts.join('  ·  ')}</span>
  );
}

export default function CockpitExposeList({ apiBase = '/api', session = null }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const accessToken = session?.access_token || null;
  const authHeaders = useMemo(() => (
    accessToken ? { Authorization: `Bearer ${accessToken}` } : {}
  ), [accessToken]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${apiBase}/cockpit/exposes`, {
        headers: { Accept: 'application/json', ...authHeaders },
      });
      setProjects(extractProjects(response.data));
    } catch (err) {
      const serverMsg = err?.response?.data?.error;
      setError(serverMsg || 'Fehler beim Laden');
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, [apiBase, authHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter((project) => {
      const haystack = [
        getProjectName(project),
        getBaseExpose(project)?.title,
        ...getVersions(project).map((v) => v?.title),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [projects, search]);

  return (
    <div style={{ flex: 1, overflowY: 'auto', background: '#F8FAFC', fontFamily: FONT_BODY }}>
      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '28px 24px 64px' }}>
        {/* Header */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 16,
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            marginBottom: 20,
          }}
        >
          <div>
            <h1 style={{ margin: 0, fontFamily: FONT_HEAD, fontSize: 24, fontWeight: 800, color: '#0E214A' }}>
              Exposés aus Projekt-Cockpit
            </h1>
            <p style={{ margin: '6px 0 0', fontSize: 13, color: '#64748B', maxWidth: 640 }}>
              Übersicht der im Projekt-Cockpit erstellten Exposés. Öffnen oder laden Sie jede Version als PDF oder HTML.
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '9px 16px',
              borderRadius: 999,
              border: '1px solid #D7DEE9',
              background: '#FFFFFF',
              color: '#0E214A',
              fontFamily: FONT_BODY,
              fontSize: 13,
              fontWeight: 700,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
            }}
          >
            <RefreshCw size={15} className={loading ? 'animate-spin' : undefined} /> Aktualisieren
          </button>
        </div>

        {/* Search */}
        <div style={{ position: 'relative', marginBottom: 20, maxWidth: 420 }}>
          <Search
            size={16}
            color="#94A3B8"
            style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }}
          />
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Nach Projekt oder Titel suchen…"
            style={{
              width: '100%',
              padding: '10px 12px 10px 36px',
              borderRadius: 12,
              border: '1px solid #D7DEE9',
              background: '#FFFFFF',
              fontFamily: FONT_BODY,
              fontSize: 13,
              color: '#0F172A',
              outline: 'none',
            }}
          />
        </div>

        {/* States */}
        {loading ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '40px 0',
              color: '#64748B',
              fontSize: 14,
            }}
          >
            <Loader2 size={18} className="animate-spin" /> Exposés werden geladen…
          </div>
        ) : error ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '16px 18px',
              borderRadius: 16,
              border: '1px solid #FECACA',
              background: '#FEF2F2',
              color: '#B91C1C',
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            <AlertCircle size={18} /> {error}
          </div>
        ) : filtered.length === 0 ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 10,
              padding: '56px 0',
              color: '#64748B',
              textAlign: 'center',
            }}
          >
            <Inbox size={30} color="#94A3B8" />
            <span style={{ fontSize: 14 }}>
              {projects.length === 0
                ? 'Noch keine Exposés im Projekt-Cockpit erstellt.'
                : 'Keine Exposés zu Ihrer Suche gefunden.'}
            </span>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {filtered.map((project) => {
              const projectId = getProjectId(project);
              const projectName = getProjectName(project);
              const base = getBaseExpose(project);
              const versions = getVersions(project);
              const cockpitLink = projectId
                ? `${COCKPIT_WEB_BASE}/projects/${encodeURIComponent(projectId)}?tab=expose`
                : null;

              return (
                <div
                  key={projectId || projectName}
                  style={{
                    border: '1px solid #D7DEE9',
                    borderRadius: 16,
                    background: '#FFFFFF',
                    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
                    overflow: 'hidden',
                  }}
                >
                  {/* Project header */}
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 12,
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '16px 20px',
                      borderBottom: '1px solid #EEF2F7',
                      background: '#FBFCFE',
                    }}
                  >
                    <h2 style={{ margin: 0, fontFamily: FONT_HEAD, fontSize: 17, fontWeight: 800, color: '#122A5C' }}>
                      {projectName}
                    </h2>
                    {cockpitLink && (
                      <a
                        href={cockpitLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          fontFamily: FONT_BODY,
                          fontSize: 12,
                          fontWeight: 700,
                          color: '#E07000',
                          textDecoration: 'none',
                        }}
                      >
                        Im Projekt-Cockpit öffnen <ExternalLink size={13} />
                      </a>
                    )}
                  </div>

                  <div style={{ padding: '8px 20px 16px' }}>
                    {/* Base exposé (Hauptexposé) */}
                    {base && (
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 12,
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '14px 0',
                          borderBottom: versions.length > 0 ? '1px solid #EEF2F7' : 'none',
                        }}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                            <span
                              style={{
                                fontFamily: FONT_BODY,
                                fontSize: 11,
                                fontWeight: 800,
                                color: '#E07000',
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                              }}
                            >
                              Hauptexposé
                            </span>
                            <span style={{ fontFamily: FONT_HEAD, fontSize: 14, fontWeight: 700, color: '#0E214A' }}>
                              {base.title || variantLabel(base.variantKey)}
                            </span>
                            <StatusBadge status={base.status} />
                          </div>
                          <MetaLine
                            price={formatPrice(base.salePrice, base.currency)}
                            date={formatDate(base.updatedAt || base.approvedAt)}
                          />
                        </div>
                        {projectId && (
                          <DocButtons
                            apiBase={apiBase}
                            authHeaders={authHeaders}
                            params={{ projectId, variant: base.variantKey || '' }}
                            baseName={`expose-${sanitizeFilenamePart(projectName || projectId)}`}
                          />
                        )}
                      </div>
                    )}

                    {/* Versions */}
                    {versions.map((version, index) => {
                      const versionId = String(version?.id ?? version?.versionId ?? '').trim();
                      return (
                        <div
                          key={versionId || `${projectId}-v${index}`}
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 12,
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '14px 0',
                            borderBottom: index < versions.length - 1 ? '1px solid #EEF2F7' : 'none',
                          }}
                        >
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                              <span
                                style={{
                                  display: 'inline-flex',
                                  padding: '2px 9px',
                                  borderRadius: 999,
                                  background: '#EFF3FA',
                                  color: '#122A5C',
                                  fontFamily: FONT_BODY,
                                  fontSize: 11,
                                  fontWeight: 800,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {variantLabel(version?.variantKey)}
                              </span>
                              <span style={{ fontFamily: FONT_HEAD, fontSize: 14, fontWeight: 600, color: '#0E214A' }}>
                                {version?.title || variantLabel(version?.variantKey)}
                              </span>
                              <StatusBadge status={version?.status} />
                            </div>
                            <MetaLine
                              price={formatPrice(version?.salePrice, version?.currency)}
                              date={formatDate(version?.updatedAt || version?.approvedAt)}
                            />
                          </div>
                          {versionId ? (
                            <DocButtons
                              apiBase={apiBase}
                              authHeaders={authHeaders}
                              params={{ versionId }}
                              baseName={`expose-${sanitizeFilenamePart(projectName || projectId)}-${sanitizeFilenamePart(version?.variantKey || versionId)}`}
                            />
                          ) : (
                            <span style={{ fontSize: 12, color: '#94A3B8' }}>Keine Dokument-ID</span>
                          )}
                        </div>
                      );
                    })}

                    {!base && versions.length === 0 && (
                      <div style={{ padding: '14px 0', fontSize: 13, color: '#94A3B8' }}>
                        Für dieses Projekt sind keine Exposé-Dokumente hinterlegt.
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
